import type { ReactNode } from "react";
import { Code } from "@connectrpc/connect";
import { useQuery } from "@connectrpc/connect-query";
import { skipToken } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";

import { formatDate, formatNepalTime } from "@yatayat/core/date";
import { formatNumber } from "@yatayat/core/money";
import {
  ComplaintService,
  ComplaintStatus,
  type GetComplaintStatusResponse,
} from "@yatayat/gen/yatayat/complaints/v1/complaints_pb";

import { ComplaintTrackForm, normalizeTrackingNo, trackingNoPattern } from "~/components/ComplaintTrackForm";
import { complaintCategoryLabels } from "~/lib/complaint-labels";
import { useI18n, type MessageKey } from "~/lib/i18n";
import { errorCode, retryTransient } from "~/lib/rpc/errors";

type TrackSearch = { filed?: boolean };

export const Route = createFileRoute("/complaints/$trackingNo")({
  validateSearch: (search: Record<string, unknown>): TrackSearch => ({
    // Set by the complaint form on success, to confirm the filing.
    filed: search.filed === true || search.filed === "true" ? true : undefined,
  }),
  component: ComplaintTracking,
});

function ComplaintTracking() {
  const { trackingNo: param } = Route.useParams();
  const { filed } = Route.useSearch();
  const { t } = useI18n();
  const trackingNo = normalizeTrackingNo(param);
  const wellFormed = trackingNoPattern.test(trackingNo);
  const query = useQuery(ComplaintService.method.getComplaintStatus, wellFormed ? { trackingNo } : skipToken, {
    retry: retryTransient,
  });

  let body: ReactNode;
  if (query.data) {
    body = <ComplaintProgress complaint={query.data} />;
  } else if (!wellFormed || (query.isError && unknownComplaint(query.error))) {
    body = (
      <div className="space-y-4">
        <p className="text-slate-600 dark:text-slate-400">{t("complaintNotFound")}</p>
        <ComplaintTrackForm />
      </div>
    );
  } else if (query.isError) {
    body = (
      <div className="space-y-3">
        <p role="alert" className="text-brand dark:text-brand-dark">
          {t("somethingWrong")}
        </p>
        <button
          type="button"
          onClick={() => void query.refetch()}
          disabled={query.isFetching}
          className="rounded-md border border-slate-300 px-4 py-2 text-sm hover:bg-slate-100 disabled:opacity-60 dark:border-slate-700 dark:hover:bg-slate-900"
        >
          {t("tryAgain")}
        </button>
      </div>
    );
  } else {
    body = <p className="text-slate-600 dark:text-slate-400">{t("loading")}</p>;
  }

  return (
    <div className="mx-auto max-w-xl space-y-4">
      <Link to="/complaints" className="text-sm text-ink underline-offset-2 hover:underline dark:text-ink-dark">
        <span aria-hidden="true">← </span>
        {t("complaintsTitle")}
      </Link>
      {filed && (
        <p role="status" className="rounded-lg bg-emerald-50 p-3 text-sm text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300">
          <span aria-hidden="true">✓ </span>
          {t("complaintFiled")}
        </p>
      )}
      <header className="rounded-xl border border-slate-200 p-5 text-center dark:border-slate-800">
        <h1 className="text-sm text-slate-600 dark:text-slate-400">{t("trackingNo")}</h1>
        <p className="mt-1 break-all font-mono text-3xl font-bold tracking-wider text-ink dark:text-ink-dark">{trackingNo}</p>
      </header>
      {body}
    </div>
  );
}

function unknownComplaint(err: unknown): boolean {
  const code = errorCode(err);
  return code === Code.NotFound || code === Code.InvalidArgument;
}

const steps: { status: ComplaintStatus; label: MessageKey }[] = [
  { status: ComplaintStatus.OPEN, label: "complaintStatusOpen" },
  { status: ComplaintStatus.ASSIGNED, label: "complaintStatusAssigned" },
  { status: ComplaintStatus.RESOLVED, label: "complaintStatusResolved" },
];

function ComplaintProgress({ complaint }: { complaint: GetComplaintStatusResponse }) {
  const { t, lang } = useI18n();
  const current = Math.max(0, steps.findIndex((s) => s.status === complaint.status));
  const resolved = complaint.status === ComplaintStatus.RESOLVED;
  const filedAt = Number(complaint.createdAtMs);
  const resolvedAt = Number(complaint.resolvedAtMs);
  const when = (ms: number) => `${formatDate(ms, lang)} · ${formatNepalTime(ms, lang)}`;

  return (
    <div className="space-y-4">
      <section className="rounded-xl border border-slate-200 p-4 dark:border-slate-800">
        <h2 className="sr-only">{t("complaintProgress")}</h2>
        <ol className="grid grid-cols-3">
          {steps.map((step, i) => {
            const done = i < current || resolved;
            const circle = done
              ? "bg-emerald-600 text-white"
              : i === current
                ? "bg-ink text-white dark:bg-ink-dark dark:text-slate-950"
                : "border-2 border-slate-300 bg-white text-slate-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-400";

            return (
              <li key={step.status} aria-current={i === current ? "step" : undefined} className="flex flex-col items-center text-center">
                <span className="relative flex w-full justify-center">
                  {i > 0 && (
                    <span
                      aria-hidden="true"
                      className={`absolute right-1/2 top-1/2 h-0.5 w-full -translate-y-1/2 ${
                        i <= current ? "bg-emerald-600" : "bg-slate-200 dark:bg-slate-700"
                      }`}
                    />
                  )}
                  <span
                    aria-hidden="true"
                    className={`relative z-10 flex h-9 w-9 items-center justify-center rounded-full text-sm font-semibold ${circle}`}
                  >
                    {done ? "✓" : formatNumber(i + 1, lang)}
                  </span>
                </span>
                <span className={`mt-2 px-1 text-sm ${i === current ? "font-semibold" : "text-slate-600 dark:text-slate-400"}`}>
                  {t(step.label)}
                  {done && <span className="sr-only"> ({t("stepDone")})</span>}
                </span>
              </li>
            );
          })}
        </ol>
        {!resolved && <p className="mt-4 text-sm text-slate-600 dark:text-slate-400">{t("complaintPendingHint")}</p>}
      </section>

      <dl className="divide-y divide-slate-100 rounded-xl border border-slate-200 text-sm dark:divide-slate-800 dark:border-slate-800">
        <Row label={t("complaintCategory")}>{t(complaintCategoryLabels[complaint.category] ?? "complaintCatOther")}</Row>
        {filedAt > 0 && <Row label={t("filedOn")}>{when(filedAt)}</Row>}
        {resolved && resolvedAt > 0 && <Row label={t("resolvedOn")}>{when(resolvedAt)}</Row>}
      </dl>

      {resolved && complaint.resolutionNote && (
        <section className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 dark:border-emerald-900 dark:bg-emerald-900/20">
          <h2 className="text-sm font-semibold text-emerald-900 dark:text-emerald-200">{t("resolutionNote")}</h2>
          <p className="mt-1 whitespace-pre-line break-words">{complaint.resolutionNote}</p>
        </section>
      )}
    </div>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 px-4 py-2.5">
      <dt className="text-slate-600 dark:text-slate-400">{label}</dt>
      <dd className="text-right font-medium">{children}</dd>
    </div>
  );
}
